/* eslint-disable no-console */
import {
  APPLE_PAY_AUTHORIZATION_TIMEOUT_MS,
  APPLE_PAY_SUCCESS_STATUSES,
  APPLE_PAY_VERSION_NUMBER,
  CURRENCY_CODE,
  PaymentStatusApplePay,
} from 'constants/';
import type {
  ApplePayPaymentRequest,
  ApplePayResult,
  MerchantSession,
  PaymentRequest,
  SendPayMethodType,
} from 'types';

const getPaymentRequest = ({
  currencyCode,
  label,
  amount,
}: PaymentRequest): ApplePayPaymentRequest => {
  const paymentRequest: ApplePayPaymentRequest = {
    currencyCode,
    countryCode: 'KZ',
    total: {
      label,
      type: 'final',
      amount,
    },
    supportedNetworks: ['masterCard', 'visa'],
    merchantCapabilities: ['supports3DS'],
  };

  return paymentRequest;
};

// Apple даёт 30 с на completePayment после авторизации, иначе закрывает шит без результата
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Apple Pay authorization timeout')), ms);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

interface PerformPaymentParams {
  ID: string;
  acquiringID: string;
  // Сумма без тиын — отображается в шите
  amount: string;
  // Сумма с тиын — уходит в процессинг
  fullAmount: string;
  sendPayment: SendPayMethodType;
  getMerchantValidation: (validationURL: string) => Promise<MerchantSession>;
  currencyCode?: string;
}

const performPayment = ({
  ID,
  acquiringID,
  amount,
  fullAmount,
  sendPayment,
  getMerchantValidation,
  currencyCode = CURRENCY_CODE,
}: PerformPaymentParams): Promise<ApplePayResult> => {
  return new Promise((resolve) => {
    const session = new ApplePaySession(
      APPLE_PAY_VERSION_NUMBER,
      getPaymentRequest({ currencyCode, label: 'Payment Highway', amount }),
    );
    let settled = false;

    const finish = (result: ApplePayResult) => {
      if (settled) {
        return;
      }

      settled = true;
      resolve(result);
    };

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await getMerchantValidation(event.validationURL);
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        console.error('Validate error', error);
        session.abort();
        finish({ status: PaymentStatusApplePay.FAILURE, error });
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        // Пока шит открыт — только запрос в процессинг. Любая навигация (3DS, redirect,
        // перезагрузка данных) до completePayment уничтожает сессию, и шит закрывается
        // без результата — поэтому она выполняется снаружи, после resolve
        const response = await withTimeout(
          sendPayment({
            ID,
            acquiringID,
            amount: fullAmount,
            payData: null,
            applepay: JSON.stringify(event.payment.token),
          }),
          APPLE_PAY_AUTHORIZATION_TIMEOUT_MS,
        );
        const isSuccess = APPLE_PAY_SUCCESS_STATUSES.includes(response.status);

        console.log('onpaymentauthorized response', response);

        if (!isSuccess) {
          console.error('Payment error in response', JSON.stringify(response));
        }

        session.completePayment(
          isSuccess ? ApplePaySession.STATUS_SUCCESS : ApplePaySession.STATUS_FAILURE,
        );
        finish({
          status: isSuccess ? PaymentStatusApplePay.SUCCESS : PaymentStatusApplePay.FAILURE,
          response,
        });
      } catch (error) {
        console.error('Payment error', error);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        finish({ status: PaymentStatusApplePay.FAILURE, error });
      }
    };

    session.oncancel = () => {
      console.log('Session cancelled');
      finish({ status: PaymentStatusApplePay.CANCEL });
    };

    session.begin();
  });
};

export { performPayment };
